/**
 * /pipeline-facility/welds/:id and /pipeline-facility/jobs/:id/welds/new —
 * one PF weld (fieldiq-api#1219, PF-3a).
 *
 * THE NDT REPORT FILE goes through the presigned flow with entityType
 * 'pf_weld': presign → PUT to R2 (putToPresignedUrl throws unless S3 answered
 * 2xx — `fetch` does not reject on an error status) → confirm (the API HEADs
 * the object) → PATCH the weld with the upload's id. Success is said only after
 * all four; any failure is said as a sentence and the weld is left as it was.
 * No Content-Type header is set by hand on any multipart call (there is none
 * here: the PUT carries the file's own type, which the URL was signed for).
 *
 * NDT links point at this tenant's NDT Inspection welds (PF_DISPATCH).
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileText, Link2, Pencil, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { api, openUploadDownload } from '../../../api/client';
import { putToPresignedUrl } from '../../../api/upload';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { usePfJob, usePfMeta } from '../../../hooks/usePipelineFacility';
import {
  useCreatePfWeld,
  useDeletePfWeld,
  useUnlinkPfNdt,
  useUpdatePfWeld,
  usePfJobRepairs,
  usePfWeld,
  usePfWeldUploads,
} from '../../../hooks/usePfDigs';
import { formatInstant } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { canDispatchPf, canWorkPf, pfDay, withUnit } from '../../../lib/pipeline-facility';
import { pfDigRefusal, pfWeldResultSkin } from '../../../lib/pf-digs';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, PF_EYEBROW, PF_SECONDARY, PfBadge, PfEmpty, PfFormError, PfLoading, PfPanel, PfSpec } from '../PfBits';
import { PfInlineConfirm } from './DigBits';
import { NdtLinkPicker } from './LinkPickers';
import { WeldForm } from './WeldForm';

/**
 * Upload a file as the weld's NDT report. Returns null on success, else a
 * sentence. Every step must succeed; nothing renders success before the PATCH.
 */
export async function uploadWeldReport(weldId: string, file: File, attach: (uploadId: string) => Promise<unknown>): Promise<string | null> {
  const contentType = file.type || 'application/octet-stream';
  let presign: { uploadUrl?: string; key?: string; uploadId?: string };
  try {
    presign = (await api.uploads.getPresignedUrl({ fileName: file.name, contentType, entityType: 'pf_weld', entityId: weldId })).data ?? {};
  } catch (err) {
    return pfDigRefusal(err, 'The upload could not be started.');
  }
  if (!presign.uploadUrl || !presign.key) return 'The upload could not be started. Try again.';
  try {
    await putToPresignedUrl(presign.uploadUrl, file, contentType);
  } catch {
    return `${file.name} did not reach storage. Check the connection and upload it again.`;
  }
  try {
    await api.uploads.confirm({ key: presign.key, size: file.size });
  } catch {
    return `${file.name} could not be confirmed in storage. Upload it again.`;
  }
  let uploadId = presign.uploadId;
  if (!uploadId) {
    const list = (await api.uploads.listForEntity('pf_weld', weldId)).data as { data?: { id: string; s3Key?: string; key?: string }[] };
    uploadId = (list?.data ?? []).find((u) => (u.s3Key ?? u.key) === presign.key)?.id;
  }
  if (!uploadId) return 'The file was stored but could not be found to attach. Upload it again.';
  try {
    await attach(uploadId);
  } catch (err) {
    return pfDigRefusal(err, 'The report was uploaded but could not be attached to the weld.');
  }
  return null;
}

export function PfNewWeldScreen() {
  const { id: jobId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const back = useBackToList(`/pipeline-facility/jobs/${jobId}?tab=welds`);
  const job = usePfJob(jobId);
  const catalog = usePfMeta().data?.data ?? null;
  const { repairs } = usePfJobRepairs(jobId);
  const create = useCreatePfWeld();
  const [error, setError] = useState<string | null>(null);
  return (
    <div data-testid="pf-weld-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={PF_EYEBROW} title="New weld" description={job.data?.data ? `${job.data.data.jobNumber} · ${job.data.data.siteName ?? '—'}` : undefined} onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px]">
          <PfPanel eyebrow="Weld" title="Record a weld">
            <WeldForm
              repairs={repairs}
              defaultRepairId={searchParams.get('digRepairId') ?? ''}
              catalog={catalog}
              meta={job.data?.meta ?? null}
              pending={create.isPending}
              error={error}
              submitLabel="Save weld"
              onCancel={back}
              onSubmit={(body) => {
                if (!jobId) return;
                setError(null);
                create.mutate(
                  { jobId, body },
                  {
                    onSuccess: (r) => {
                      toast.success(`Weld ${r.data?.weldNumber ?? ''} saved`.trim());
                      navigate(r.data ? `/pipeline-facility/welds/${r.data.id}` : `/pipeline-facility/jobs/${jobId}?tab=welds`, { replace: true });
                    },
                    onError: (err) => setError(pfDigRefusal(err, 'The weld could not be saved.')),
                  },
                );
              }}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}

export default function PfWeldDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/pipeline-facility/welds');
  const { user } = useAuth();
  const canDispatch = canDispatchPf(user?.role);
  const query = usePfWeld(id);
  const catalog = usePfMeta().data?.data ?? null;
  const weld = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const job = usePfJob(weld?.jobId);
  const jobOpen = !!job.data?.data && ['draft', 'scheduled', 'in_progress'].includes(job.data.data.status);
  const canWork = canWorkPf(user?.role) && jobOpen;
  const { repairs } = usePfJobRepairs(editingJobId(weld?.jobId, canWork));
  const uploads = usePfWeldUploads(weld?.id, !!weld?.ndtReportFileKey);
  const update = useUpdatePfWeld();
  const del = useDeletePfWeld();
  const unlink = useUnlinkPfNdt();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [picking, setPicking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const failure = queryLoadFailure(query);
  const reportUpload = (uploads.data ?? []).find((u) => (u.s3Key ?? u.key) === weld?.ndtReportFileKey);

  const onFile = async (file: File | undefined) => {
    if (!file || !weld) return;
    setUploadError(null);
    setUploading(true);
    const problem = await uploadWeldReport(weld.id, file, (uploadId) => update.mutateAsync({ id: weld.id, body: { ndt_report_upload_id: uploadId } }));
    setUploading(false);
    if (problem) setUploadError(problem);
    else toast.success('NDT report attached');
  };

  return (
    <div data-testid="pf-weld-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={weld ? `${PF_EYEBROW} · ${weld.jobNumber ?? 'Weld'}` : PF_EYEBROW}
        title={weld?.weldNumber || (query.isLoading ? 'Weld' : '—')}
        description={weld ? `${weld.digNumber ? `${weld.digNumber} · ` : ''}${weld.welderName ?? 'Welder not recorded'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          weld ? (
            <div className="flex flex-wrap gap-2">
              {canWork && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); }} disabled={editing}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit
                </button>
              )}
              {canDispatch && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <PfLoading label="Loading weld" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !weld) ? (
            <PfPanel eyebrow="Weld" title="Weld">
              <PfEmpty>This weld was not found. It may have been deleted.</PfEmpty>
            </PfPanel>
          ) : failure || !weld ? (
            <PfPanel eyebrow="Weld" title="Weld">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this weld" />
            </PfPanel>
          ) : (
            <>
              {deleting && (
                <PfInlineConfirm
                  testId="pf-weld-delete-confirm"
                  message={`Delete weld ${weld.weldNumber}? Its NDT links go with it.`}
                  confirmLabel="Delete weld"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(weld.id, {
                      onSuccess: () => {
                        toast.success('Weld deleted');
                        navigate(`/pipeline-facility/jobs/${weld.jobId}?tab=welds`, { replace: true });
                      },
                      onError: (err) => setDeleteError(pfDigRefusal(err, 'The weld could not be deleted.')),
                    })
                  }
                />
              )}
              <PfPanel eyebrow="Weld" title="Details" testId="pf-weld-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <PfSpec label="NDT result" value={<PfBadge skin={pfWeldResultSkin(weld.ndtResult)} value={weld.ndtResult} />} />
                  <PfSpec label="Job" value={<Link className="underline" to={`/pipeline-facility/jobs/${weld.jobId}?tab=welds`}>{weld.jobNumber ?? '—'}</Link>} />
                  <PfSpec label="Dig" value={weld.digId ? <Link className="underline" to={`/pipeline-facility/digs/${weld.digId}`}>{weld.digNumber ?? '—'}</Link> : '—'} />
                  <PfSpec label="Chainage" value={withUnit(weld.chainage, meta?.units?.chainage)} mono />
                  <PfSpec label="Welder" value={weld.welderName || '—'} />
                  <PfSpec label="Stamp" value={weld.welderStamp || '—'} mono />
                  <PfSpec label="WPS" value={weld.wpsRef || '—'} mono />
                  <PfSpec label="Heat numbers" value={weld.heatNumbers || '—'} mono />
                  <PfSpec label="Welded" value={pfDay(weld.weldDate)} />
                  <PfSpec label="NDT company" value={weld.ndtCompany || '—'} />
                  <PfSpec label="NDT report ref" value={weld.ndtReportRef || '—'} mono />
                  <PfSpec label="Report file" value={weld.ndtReportFileKey ? `Attached ${formatInstant(weld.ndtReportUploadedAt, meta?.timeZone) ?? ''}`.trim() : '—'} />
                </dl>
                {weld.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{weld.notes}</p>}
              </PfPanel>
              {editing && canWork && (
                <PfPanel eyebrow="Edit" title="Edit weld">
                  <WeldForm
                    weld={weld}
                    repairs={repairs}
                    catalog={catalog}
                    meta={meta}
                    pending={update.isPending}
                    error={editError}
                    submitLabel="Save weld"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      update.mutate(
                        { id: weld.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Weld saved');
                            setEditing(false);
                          },
                          onError: (err) => setEditError(pfDigRefusal(err, 'The weld could not be saved.')),
                        },
                      );
                    }}
                  />
                </PfPanel>
              )}
              <PfPanel eyebrow="Outside NDT" title="NDT report file" testId="pf-weld-report-file">
                <div className="space-y-3 px-4 py-4">
                  <PfFormError message={uploadError} />
                  <div className="flex flex-wrap items-center gap-2">
                    {weld.ndtReportFileKey ? (
                      <button
                        type="button"
                        className={PF_SECONDARY}
                        disabled={!reportUpload}
                        onClick={() => {
                          if (reportUpload) openUploadDownload(reportUpload.id).catch(() => setUploadError('The report file could not be opened. Try again.'));
                        }}
                      >
                        <FileText className="size-4" aria-hidden="true" />
                        Open NDT report
                      </button>
                    ) : (
                      <p className="text-sm text-fieldiq-text">No report file attached.</p>
                    )}
                    {canWork && (
                      <label className={`${PF_SECONDARY} cursor-pointer`}>
                        <Upload className="size-4" aria-hidden="true" />
                        {uploading ? 'Uploading…' : weld.ndtReportFileKey ? 'Replace file' : 'Upload report'}
                        <input
                          type="file"
                          className="sr-only"
                          aria-label="NDT report file"
                          accept="application/pdf,image/*"
                          disabled={uploading}
                          onChange={(e) => {
                            void onFile(e.target.files?.[0]);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </PfPanel>
              <PfPanel
                eyebrow="NDT Inspection"
                title={`Linked NDT welds (${weld.ndtLinks.length})`}
                testId="pf-weld-ndt-links"
                actions={
                  canDispatch && !picking ? (
                    <button type="button" className={PF_SECONDARY} onClick={() => setPicking(true)}>
                      <Link2 className="size-4" aria-hidden="true" />
                      Link NDT weld
                    </button>
                  ) : undefined
                }
              >
                <div className="space-y-3 px-4 py-4">
                  <PfFormError message={linkError} />
                  {picking && <NdtLinkPicker weldId={weld.id} onDone={() => setPicking(false)} />}
                  {weld.ndtLinks.length === 0 ? (
                    <p className="text-sm text-fieldiq-text">Not linked to an NDT Inspection weld.</p>
                  ) : (
                    <ul className="divide-y divide-fieldiq-border">
                      {weld.ndtLinks.map((l) => (
                        <li key={l.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid="pf-weld-ndt-link">
                          <span className="font-mono font-bold text-white">{l.ndtWeldMissing ? 'Weld removed in NDT' : l.ndtWeldNumber || '—'}</span>
                          <span className="text-fieldiq-text">{l.ndtJobNumber || '—'}</span>
                          <span className="text-fieldiq-muted">Latest exam: {l.ndtLatestResult || '—'}</span>
                          {canDispatch && (
                            <button
                              type="button"
                              className="ml-auto text-xs font-bold text-fieldiq-muted underline"
                              onClick={() => {
                                setLinkError(null);
                                unlink.mutate(l.id, {
                                  onSuccess: () => toast.success('NDT weld unlinked'),
                                  onError: (err) => setLinkError(pfDigRefusal(err, 'The NDT weld could not be unlinked.')),
                                });
                              }}
                            >
                              Unlink
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </PfPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

/** Load the job's repairs only for someone who can edit the weld. */
function editingJobId(jobId: string | undefined, canWork: boolean) {
  return canWork ? jobId : undefined;
}
